import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  NgZone,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import * as monaco from "monaco-editor";
import { ThemeService } from "../theme/theme.service";
import type { AppTheme } from "../theme/theme.interface";
import { FontOption, StyleInput } from "./editor.interface";

const FONT_SIZE_VALUES = [
  "10px",
  "13px",
  "16px",
  "18px",
  "24px",
  "32px",
  "48px",
];
const LEGACY_FONT_SIZE_TO_PX: Record<string, string> = FONT_SIZE_VALUES.reduce(
  (acc, size, index) => {
    acc[String(index + 1)] = size;
    return acc;
  },
  {} as Record<string, string>,
);

@Component({
  selector: "app-editor",
  templateUrl: "./editor.component.html",
  styleUrls: ["./editor.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class EditorComponent
  implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy
{
  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private themeService: ThemeService,
  ) {}
  @Input() height = 300;
  @Input() sourceOnly = false;
  @ViewChild("editor", { static: false }) editor: ElementRef<HTMLDivElement>;
  @ViewChild("monacoContainer", { static: false })
  monacoContainer: ElementRef<HTMLDivElement>;

  disabled = false;

  readonly fontFamilies: FontOption[] = [
    { name: "Default", value: "inherit" },
    { name: "Arial", value: "Arial, Helvetica, sans-serif" },
    { name: "Verdana", value: "Verdana, Geneva, sans-serif" },
    { name: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
    { name: "Times New Roman", value: '"Times New Roman", Times, serif' },
    { name: "Georgia", value: "Georgia, serif" },
    { name: "Courier New", value: '"Courier New", Courier, monospace' },
  ];

  readonly fontSizes: FontOption[] = [
    ...FONT_SIZE_VALUES.map((size) => ({ name: size, value: size })),
  ];

  showSource = false;
  showLinkInput = false;
  showImageInput = false;
  showTableProperties = false;
  showCellProperties = false;
  linkInputUrl = "";
  imageInputUrl = "https://";
  imageInputAlt = "";
  imageInputWidth = "";
  imageInputHref = "";

  selectedCell: HTMLTableCellElement | null = null;
  selectedTable: HTMLTableElement | null = null;
  selectedImage: HTMLImageElement | null = null;
  selectedLink: HTMLAnchorElement | null = null;
  hasTableInEditor = false;
  isBold = false;
  isItalic = false;
  isUnderline = false;
  isJustifyLeft = false;
  isJustifyCenter = false;
  isJustifyRight = false;

  currentFontSize = "16px";
  currentFontName = "";
  currentContent = "";
  currentCellAlign = "";
  currentCellValign = "";

  private savedRange: Range | null = null;
  private initialValue = "";
  private destroy$ = new Subject<void>();
  private styleInputSubject = new Subject<StyleInput>();
  private monacoEditor: monaco.editor.IStandaloneCodeEditor | null = null;
  private monacoThemesDefined = false;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};
  private selectionChangeHandler = () => this.onSelectionChange();

  ngOnInit() {
    document.addEventListener("selectionchange", this.selectionChangeHandler);
    this.configureMonaco();

    this.themeService.theme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme) => {
        this.applyMonacoTheme(theme);
      });

    this.styleInputSubject
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((input) => {
        const { value } = input;
        const handlers: Record<StyleInput["type"], () => void> = {
          cellWidth: () => this.updateCellWidth(value),
          cellHeight: () => this.updateCellHeight(value),
          cellPadding: () => this.updateCellPadding(value),
          cellPaddingTop: () => this.updateCellPaddingSide("top", value),
          cellPaddingRight: () => this.updateCellPaddingSide("right", value),
          cellPaddingBottom: () => this.updateCellPaddingSide("bottom", value),
          cellPaddingLeft: () => this.updateCellPaddingSide("left", value),
          lineHeight: () => this.updateLineHeight(value),
          tableWidth: () => this.updateTableWidth(value),
          tableHeight: () => this.updateTableHeight(value),
          tableBorderSpacing: () => this.updateTableBorderSpacing(value),
          bgImage: () =>
            this.updateBackgroundProperty(
              "backgroundImage",
              value,
              input.target,
            ),
          bgPosition: () =>
            this.updateBackgroundProperty(
              "backgroundPosition",
              value,
              input.target,
            ),
          bgRepeat: () =>
            this.updateBackgroundProperty(
              "backgroundRepeat",
              value,
              input.target,
            ),
          bgSize: () =>
            this.updateBackgroundProperty(
              "backgroundSize",
              value,
              input.target,
            ),
        };
        handlers[input.type]?.();
      });
  }

  private configureMonaco() {
    if (typeof window === "undefined") {
      return;
    }

    const win = window as unknown as {
      MonacoEnvironment?: {
        getWorker: (moduleId: string, label: string) => Worker;
      };
    };
    win.MonacoEnvironment = {
      getWorker: (_: string, label: string) => {
        const baseHref =
          document.querySelector("base")?.getAttribute("href") || "/";
        const workerPath =
          label === "html"
            ? "assets/monaco-editor/vs/language/html/html.worker.js"
            : "assets/monaco-editor/vs/editor/editor.worker.js";
        const workerUrl = new URL(workerPath, window.location.origin + baseHref)
          .href;
        return this.ngZone.runOutsideAngular(() => {
          return new Worker(workerUrl, { type: "module" });
        });
      },
    };

    try {
      const htmlLang = (
        monaco.languages as unknown as {
          html?: {
            htmlDefaults?: {
              setOptions: (options: unknown) => void;
              setModeConfiguration: (options: unknown) => void;
              modeConfiguration?: Record<string, unknown>;
            };
          };
        }
      ).html;
      if (htmlLang?.htmlDefaults) {
        htmlLang.htmlDefaults.setOptions({
          format: {
            tabSize: 2,
            insertSpaces: true,
            wrapLineLength: 0,
            unformatted: "wbr",
            contentUnformatted: "pre,textarea",
            indentInnerHtml: false,
            preserveNewLines: true,
            maxPreserveNewLines: 1,
            indentHandlebars: false,
            endWithNewline: false,
            extraLiners: "",
            wrapAttributes: "auto",
          },
          suggest: {
            html5: true,
            angular1: false,
            ionic: false,
          },
          validate: true,
          schemas: [],
        });
        htmlLang.htmlDefaults.setModeConfiguration({
          ...(htmlLang.htmlDefaults.modeConfiguration ?? {}),
          documentFormattingEdits: false,
          documentRangeFormattingEdits: false,
        });
      }
    } catch (e) {
      console.warn("Monaco HTML configuration error", e);
    }

    this.ensureMonacoThemes();
    this.applyMonacoTheme(this.themeService.theme);
  }

  private ensureMonacoThemes() {
    if (this.monacoThemesDefined) {
      return;
    }
    monaco.editor.defineTheme("dbc1-light", {
      base: "vs",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#ffffff",
        "editorGutter.background": "#ffffff",
        "editorLineNumber.foreground": "#8a8a8a",
        "editor.lineHighlightBackground": "#f5f5f5",
      },
    });
    monaco.editor.defineTheme("dbc1-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#17171a",
        "editorGutter.background": "#17171a",
        "editorLineNumber.foreground": "#6b6b6b",
        "editor.lineHighlightBackground": "#1f1f24",
        "editorCursor.foreground": "#f3f3f3",
        "editor.selectionBackground": "#3a3a40",
      },
    });
    this.monacoThemesDefined = true;
  }

  private monacoThemeName(theme: AppTheme): string {
    return theme === "dark" ? "dbc1-dark" : "dbc1-light";
  }

  private applyMonacoTheme(theme: AppTheme) {
    if (typeof window === "undefined") {
      return;
    }
    this.ensureMonacoThemes();
    monaco.editor.setTheme(this.monacoThemeName(theme));
  }

  ngAfterViewInit() {
    if (this.sourceOnly) {
      this.showSource = true;
      const content = this.initialValue || this.currentContent;
      if (content?.trim()) {
        this.currentContent = content;
      }
      this.cdr.detectChanges();
      setTimeout(() => {
        this.initMonacoEditor();
      }, 50);
      return;
    }

    const editorElement = this.getEditorElement();
    if (!editorElement) {
      return;
    }

    const content = this.initialValue || this.currentContent;
    if (content && content.trim()) {
      editorElement.innerHTML = this.processHtmlForEditor(content);
    } else {
      editorElement.innerHTML = "";
      this.onInput();
    }

    this.setupImageClickHandler();
    this.checkForTablesInEditor();
  }

  private setupImageClickHandler() {
    const editorElement = this.getEditorElement();
    if (!editorElement) {
      return;
    }

    editorElement.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "IMG") {
        event.preventDefault();
        this.editImage(target as HTMLImageElement);
      } else if (target.tagName === "A") {
        event.preventDefault();
        this.editLink(target as HTMLAnchorElement);
      }
    });
  }

  ngOnDestroy() {
    document.removeEventListener(
      "selectionchange",
      this.selectionChangeHandler,
    );
    this.destroyMonacoEditor();
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string): void {
    const val = value || "";
    this.currentContent = val;

    if (this.sourceOnly) {
      if (this.monacoEditor) {
        this.monacoEditor.setValue(val);
      }
      return;
    }

    if (this.showSource && this.monacoEditor) {
      this.monacoEditor.setValue(val);
    }

    const editorElement = this.getEditorElement();
    if (editorElement) {
      if (val && val.trim()) {
        editorElement.innerHTML = this.processHtmlForEditor(val);
      } else {
        editorElement.innerHTML = "";
        this.onInput();
      }
    } else {
      if (val && val.trim()) {
        this.initialValue = this.processHtmlForEditor(val);
      } else {
        this.initialValue = "";
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    const editorElement = this.getEditorElement();
    if (editorElement) {
      editorElement.contentEditable = isDisabled ? "false" : "true";
    }
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    const editorElement = this.getEditorElement();
    if (!editorElement || this.showSource) {
      return;
    }

    const isInsideEditor = editorElement.contains(event.target as Node);
    if (!isInsideEditor) {
      return;
    }

    const isUndo =
      (event.ctrlKey || event.metaKey) && event.key === "z" && !event.shiftKey;
    const isRedo =
      (event.ctrlKey || event.metaKey) &&
      (event.key === "y" || (event.key === "z" && event.shiftKey));

    if (isUndo) {
      event.preventDefault();
      this.undo();
    } else if (isRedo) {
      event.preventDefault();
      this.redo();
    }
  }

  onSelectionChange() {
    if (this.showSource) {
      return;
    }

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const editorElement = this.getEditorElement();
    if (!editorElement) {
      return;
    }

    const node = selection.anchorNode;
    if (!this.isNodeInsideEditor(node, editorElement)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const newSelectedCell = this.getSelectedElement(
          "td",
        ) as HTMLTableCellElement;
        const newSelectedTable = this.getSelectedElement(
          "table",
        ) as HTMLTableElement;

        const hadTable = !!this.selectedTable;

        this.ngZone.run(() => {
          this.selectedCell = newSelectedCell;
          this.selectedTable = newSelectedTable;

          if (!this.selectedCell && this.showCellProperties) {
            this.showCellProperties = false;
            if (this.selectedTable && hadTable) {
              this.showTableProperties = true;
            }
          }

          if (!this.selectedTable && this.showTableProperties) {
            this.showTableProperties = false;
          }

          if (
            this.selectedCell &&
            this.selectedTable &&
            this.showTableProperties &&
            !this.showCellProperties
          ) {
            this.showCellProperties = true;
            this.showTableProperties = false;
          }

          this.checkForTablesInEditor();
          this.updateCellAlignmentState();
          this.updateToolbarState();
        });
      }, 0);
    });
  }

  private checkForTablesInEditor() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const editorElement = this.getEditorElement();
        if (!editorElement) {
          this.ngZone.run(() => {
            this.hasTableInEditor = false;
          });
          return;
        }
        const hasTable = editorElement.querySelector("table") !== null;
        this.ngZone.run(() => {
          this.hasTableInEditor = hasTable;
        });
      }, 0);
    });
  }

  updateToolbarState() {
    this.isBold = document.queryCommandState("bold");
    this.isItalic = document.queryCommandState("italic");
    this.isUnderline = document.queryCommandState("underline");
    this.isJustifyLeft = document.queryCommandState("justifyLeft");
    this.isJustifyCenter = document.queryCommandState("justifyCenter");
    this.isJustifyRight = document.queryCommandState("justifyRight");

    this.currentFontName = this.getCurrentFontName();
    this.currentFontSize = this.getCurrentFontSize();

    this.cdr.markForCheck();
  }

  private getCurrentFontName(): string {
    const fontNameValue = document.queryCommandValue("fontName");
    if (fontNameValue) {
      return this.findMatchingFont(fontNameValue.replace(/['"]/g, ""));
    }

    const element = this.getSelectedElementForColor();
    if (element) {
      return this.findMatchingFont(window.getComputedStyle(element).fontFamily);
    }

    return "";
  }

  private getCurrentFontSize(): string {
    const element = this.getSelectedElementForColor();
    if (!element) {
      return "";
    }

    const computedSize = window.getComputedStyle(element).fontSize;
    const sizeMatch = computedSize.match(/(\d+)px/);
    if (!sizeMatch) {
      return "";
    }

    const pxSize = `${parseInt(sizeMatch[1], 10)}px`;
    return this.fontSizes.some((size) => size.value === pxSize) ? pxSize : "";
  }

  toggleSource() {
    if (this.sourceOnly) {
      return;
    }
    if (!this.showSource) {
      const editorElement = this.getEditorElement();
      if (editorElement) {
        this.currentContent = this.processHtmlForOutput(
          editorElement.innerHTML,
        );
      }
      this.showSource = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.monacoContainer?.nativeElement) {
          this.initMonacoEditor();
        } else {
          setTimeout(() => {
            this.initMonacoEditor();
          }, 200);
        }
      }, 50);
    } else {
      if (this.monacoEditor) {
        this.currentContent = this.monacoEditor.getValue();
      }
      this.destroyMonacoEditor();
      this.showSource = false;
      this.cdr.detectChanges();
      setTimeout(() => {
        const editorElement = this.getEditorElement();
        if (editorElement) {
          editorElement.innerHTML = this.processHtmlForEditor(
            this.currentContent || "",
          );
        }
      }, 0);
    }
  }

  onSourceChange(newValue: string) {
    const normalizedValue = this.normalizeLegacyFontMarkup(newValue);
    this.currentContent = normalizedValue;
    this.onChange(normalizedValue);
  }

  onSourceInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.onSourceChange(target.value);
  }

  private initMonacoEditor() {
    if (!this.monacoContainer?.nativeElement) {
      return;
    }

    if (this.monacoEditor) {
      this.monacoEditor.dispose();
      this.monacoEditor = null;
    }

    const container = this.monacoContainer.nativeElement;
    if (
      !container ||
      container.offsetWidth === 0 ||
      container.offsetHeight === 0
    ) {
      setTimeout(() => {
        this.initMonacoEditor();
      }, 100);
      return;
    }
    const editorOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
      value: this.currentContent || "",
      language: "html",
      theme: this.monacoThemeName(this.themeService.theme),
      fixedOverflowWidgets: true,
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      scrollbar: {
        verticalScrollbarSize: 12,
        horizontalScrollbarSize: 12,
        useShadows: false,
      },
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      wordWrap: "on",
      lineNumbers: "on",
      fontSize: 13,
      fontFamily: "'Menlo', 'Monaco', 'Courier New', monospace",
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: false,
      formatOnType: false,
      autoIndent: this.sourceOnly ? "keep" : "advanced",
      bracketPairColorization: { enabled: true },
      autoClosingBrackets: "always",
      autoClosingQuotes: "always",
      autoClosingOvertype: "always",
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true,
      },
      suggestOnTriggerCharacters: true,
      acceptSuggestionOnEnter: "smart",
      tabCompletion: "on",
      wordBasedSuggestions: "allDocuments",
      parameterHints: {
        enabled: true,
      },
      autoSurround: "languageDefined",
      suggestSelection: "first",
      snippetSuggestions: "top",
      linkedEditing: true,
    };

    this.monacoEditor = monaco.editor.create(container, editorOptions);

    this.monacoEditor.onDidChangeModelContent((event) => {
      const value = this.monacoEditor?.getValue() || "";
      this.onSourceChange(value);

      if (this.monacoEditor && event.changes.length > 0) {
        const lastChange = event.changes[event.changes.length - 1];
        if (lastChange && lastChange.text === ">") {
          const model = this.monacoEditor.getModel();
          if (model) {
            const position = {
              lineNumber: lastChange.range.startLineNumber,
              column: lastChange.range.startColumn + 1,
            };

            const lineContent = model.getLineContent(position.lineNumber);
            const textBefore = lineContent.substring(0, position.column);

            const tagMatch = textBefore.match(
              /<([a-zA-Z0-9\-]+)(?:\s+[^>]*)?>$/,
            );
            if (tagMatch) {
              const tagName = tagMatch[1];
              const voidElements = [
                "area",
                "base",
                "br",
                "col",
                "embed",
                "hr",
                "img",
                "input",
                "link",
                "meta",
                "param",
                "source",
                "track",
                "wbr",
              ];

              if (!voidElements.includes(tagName.toLowerCase())) {
                const closingTag = `</${tagName}>`;
                this.monacoEditor.executeEdits("auto-close-tag", [
                  {
                    range: new monaco.Range(
                      position.lineNumber,
                      position.column,
                      position.lineNumber,
                      position.column,
                    ),
                    text: closingTag,
                    forceMoveMarkers: true,
                  },
                ]);
                this.monacoEditor.setPosition(position);
              }
            }
          }
        }
      }
    });
  }

  private destroyMonacoEditor() {
    if (this.monacoEditor) {
      this.monacoEditor.dispose();
      this.monacoEditor = null;
    }
  }

  onSelectChange(command: string, event: Event) {
    const target = event.target as HTMLSelectElement;
    this.execCommand(command, target.value);
  }

  onColorChange(
    method: "setTextColor" | "setBackgroundColor" | "setCellBackgroundColor",
    event: Event,
  ) {
    const target = event.target as HTMLInputElement;
    if (method === "setTextColor") {
      this.setTextColor(target.value);
    } else if (method === "setBackgroundColor") {
      this.setBackgroundColor(target.value);
    } else {
      this.setCellBackgroundColor(target.value);
    }
  }

  onStyleInputChange(
    type: StyleInput["type"],
    event: Event,
    target?: "cell" | "table",
  ) {
    const targetEl = event.target as HTMLInputElement | HTMLSelectElement;
    this.onStyleInput(type, targetEl.value, target);
  }

  onStyleInput(
    type: StyleInput["type"],
    value: string,
    target?: "cell" | "table",
  ) {
    this.styleInputSubject.next({ type, value, target });
  }

  onInput() {
    const editorElement = this.getEditorElement();
    if (!editorElement) {
      return;
    }

    const html = this.processHtmlForOutput(editorElement.innerHTML);
    this.currentContent = html;
    this.onChange(html);
    this.checkForTablesInEditor();
    this.updateToolbarState();
  }

  execCommand(command: string, value: string | null = null) {
    if (
      this.selectedCell &&
      (command === "justifyLeft" ||
        command === "justifyCenter" ||
        command === "justifyRight")
    ) {
      const alignMap: Record<string, string> = {
        justifyLeft: "left",
        justifyCenter: "center",
        justifyRight: "right",
      };
      this.updateCellHorizontalAlign(alignMap[command]);
      return;
    }

    this.focusEditor();
    this.setStyleWithCssMode(true);

    let finalValue = value;
    if (value && (command === "foreColor" || command === "hiliteColor")) {
      const trimmedValue = value.trim();
      if (
        trimmedValue === "transparent" ||
        trimmedValue === "rgba(0, 0, 0, 0)"
      ) {
        return;
      }
      finalValue = this.normalizeColorToHex(value);
    }

    if (command === "fontSize" && finalValue) {
      this.applyFontSize(finalValue);
      this.onInput();
      return;
    }

    document.execCommand(command, false, finalValue);
    this.onInput();
  }

  private applyFontSize(fontSize: string) {
    const normalizedFontSize = this.normalizeSizeValue(fontSize, false);
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      const span = document.createElement("span");
      span.style.fontSize = normalizedFontSize;
      span.innerHTML = "&nbsp;";
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.setStart(span, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      return;
    }

    const selectedContent = range.extractContents();
    const span = document.createElement("span");
    span.style.fontSize = normalizedFontSize;
    span.appendChild(selectedContent);
    range.insertNode(span);
    selection.removeAllRanges();
  }

  setTextColor(color: string) {
    if (
      !color ||
      color.trim() === "transparent" ||
      color.trim() === "rgba(0, 0, 0, 0)"
    ) {
      this.removeTextColor();
      return;
    }
    const hexColor = this.normalizeColorToHex(color);
    this.execCommand("foreColor", hexColor);
  }

  removeTextColor() {
    this.focusEditor();
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      return;
    }

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (treeNode) => {
          return range.intersectsNode(treeNode)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
    );

    const elements: HTMLElement[] = [];
    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode.nodeType === 1) {
        elements.push(currentNode as HTMLElement);
      }
      currentNode = walker.nextNode();
    }

    const parent =
      range.commonAncestorContainer.nodeType === 1
        ? (range.commonAncestorContainer as HTMLElement)
        : range.commonAncestorContainer.parentElement;

    if (parent && range.intersectsNode(parent)) {
      elements.push(parent);
    }

    elements.forEach((el) => {
      if (el.style.color) {
        el.style.removeProperty("color");
      }
      if (el.hasAttribute("color")) {
        el.removeAttribute("color");
      }
    });

    this.onInput();
  }

  setBackgroundColor(color: string) {
    if (
      !color ||
      color.trim() === "transparent" ||
      color.trim() === "rgba(0, 0, 0, 0)"
    ) {
      this.removeBackgroundColor();
      return;
    }
    const hexColor = this.normalizeColorToHex(color);
    this.execCommand("hiliteColor", hexColor);
  }

  removeBackgroundColor() {
    this.focusEditor();
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      return;
    }

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (treeNode) => {
          return range.intersectsNode(treeNode)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      },
    );

    const elements: HTMLElement[] = [];
    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode.nodeType === 1) {
        elements.push(currentNode as HTMLElement);
      }
      currentNode = walker.nextNode();
    }

    const parent =
      range.commonAncestorContainer.nodeType === 1
        ? (range.commonAncestorContainer as HTMLElement)
        : range.commonAncestorContainer.parentElement;

    if (parent && range.intersectsNode(parent)) {
      elements.push(parent);
    }

    elements.forEach((el) => {
      if (el.style.backgroundColor) {
        el.style.removeProperty("background-color");
      }
    });

    this.onInput();
  }

  private getSelectedElementForColor(): HTMLElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return null;
    }

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    return container.nodeType === 3
      ? container.parentElement
      : (container as HTMLElement);
  }

  hasTextColor(): boolean {
    const element = this.getSelectedElementForColor();
    if (!element) {
      return false;
    }

    return !!(element.style.color || element.hasAttribute("color"));
  }

  hasBackgroundColor(): boolean {
    const element = this.getSelectedElementForColor();
    if (!element) {
      return false;
    }

    const bgColor = element.style.backgroundColor;
    return !!(
      bgColor &&
      bgColor !== "transparent" &&
      bgColor !== "rgba(0, 0, 0, 0)"
    );
  }

  getCurrentTextColor(): string {
    const element = this.getSelectedElementForColor();
    if (!element) {
      return "#000000";
    }

    if (element.style.color) {
      return this.normalizeColorToHex(element.style.color);
    }

    const computedColor = window.getComputedStyle(element).color;
    return this.normalizeColorToHex(computedColor);
  }

  getCurrentBackgroundColor(): string {
    const element = this.getSelectedElementForColor();
    if (!element) {
      return "#ffffff";
    }

    if (
      element.style.backgroundColor &&
      element.style.backgroundColor !== "transparent"
    ) {
      return this.normalizeColorToHex(element.style.backgroundColor);
    }

    const computedBg = window.getComputedStyle(element).backgroundColor;
    if (computedBg === "transparent" || computedBg === "rgba(0, 0, 0, 0)") {
      return "#ffffff";
    }

    return this.normalizeColorToHex(computedBg);
  }

  private normalizeColorToHex(color: string): string {
    if (!color) {
      return "#000000";
    }

    color = color.trim();

    if (color.startsWith("#")) {
      if (color.length === 4) {
        return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
      }
      return color.length === 7 ? color : "#000000";
    }

    if (color.startsWith("rgb") || color.startsWith("rgba")) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0], 10);
        const g = parseInt(matches[1], 10);
        const b = parseInt(matches[2], 10);
        return `#${this.componentToHex(r)}${this.componentToHex(g)}${this.componentToHex(b)}`;
      }
    }

    if (color === "transparent" || color === "inherit") {
      return "#000000";
    }

    const tempDiv = document.createElement("div");
    tempDiv.style.color = color;
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);

    if (computedColor.startsWith("rgb")) {
      const matches = computedColor.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0], 10);
        const g = parseInt(matches[1], 10);
        const b = parseInt(matches[2], 10);
        return `#${this.componentToHex(r)}${this.componentToHex(g)}${this.componentToHex(b)}`;
      }
    }

    return "#000000";
  }

  private componentToHex(c: number): string {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }

  execValign(align: string) {
    if (this.selectedCell) {
      this.updateCellVerticalAlign(align);
    }
  }

  insertHtml(html: string) {
    this.focusEditor();
    document.execCommand("insertHTML", false, html);
    this.onInput();
  }

  getEditorInstance() {
    return this.getEditorElement();
  }

  insertLink() {
    this.saveSelection();
    this.showLinkInput = !this.showLinkInput;
    this.showImageInput = false;
    this.showTableProperties = false;
    this.showCellProperties = false;
    this.linkInputUrl = "https://";
    this.selectedLink = null;
  }

  editLink(link: HTMLAnchorElement) {
    this.selectedLink = link;
    this.showLinkInput = true;
    this.showImageInput = false;
    this.showTableProperties = false;
    this.showCellProperties = false;

    this.linkInputUrl = link.href || "https://";
  }

  applyLink() {
    if (!this.linkInputUrl) {
      this.showLinkInput = false;
      return;
    }

    if (this.selectedLink) {
      this.selectedLink.href = this.linkInputUrl;
      this.selectedLink = null;
      this.showLinkInput = false;
      this.onInput();
      return;
    }

    this.restoreSelection();
    this.execCommand("createLink", this.linkInputUrl);
    this.showLinkInput = false;
  }

  cancelLink() {
    this.showLinkInput = false;
    this.restoreSelection();
  }

  cancelTableProperties() {
    this.showTableProperties = false;
  }

  insertImage() {
    this.saveSelection();
    this.showImageInput = !this.showImageInput;
    this.showLinkInput = false;
    this.showTableProperties = false;
    this.showCellProperties = false;
    this.imageInputUrl = "https://";
    this.imageInputAlt = "";
    this.imageInputWidth = "";
    this.imageInputHref = "";
    this.selectedImage = null;
  }

  editImage(img: HTMLImageElement) {
    this.selectedImage = img;
    this.showImageInput = true;
    this.showLinkInput = false;
    this.showTableProperties = false;
    this.showCellProperties = false;

    this.imageInputUrl = img.src || "https://";
    this.imageInputAlt = img.alt || "";

    const width = img.style.width || img.getAttribute("width") || "";
    this.imageInputWidth = width.replace(/px|%/g, "") || "";

    const parentLink =
      img.parentElement?.tagName === "A"
        ? (img.parentElement as HTMLAnchorElement)
        : null;
    this.imageInputHref = parentLink?.href || "";
  }

  toggleTableProperties() {
    const isCurrentlyVisible =
      this.showTableProperties || this.showCellProperties;
    const shouldShow = !isCurrentlyVisible;

    if (this.hasTableInEditor && !this.selectedTable) {
      const editorElement = this.getEditorElement();
      if (editorElement) {
        const firstTable = editorElement.querySelector("table");
        if (firstTable) {
          this.selectedTable = firstTable;
        }
      }
    }

    if (shouldShow) {
      if (this.selectedCell && this.selectedTable) {
        this.showCellProperties = true;
        this.showTableProperties = false;
      } else if (this.selectedCell) {
        this.showCellProperties = true;
        this.showTableProperties = false;
      } else if (this.selectedTable) {
        this.showTableProperties = true;
        this.showCellProperties = false;
      }
    } else {
      this.showTableProperties = false;
      this.showCellProperties = false;
    }

    this.showLinkInput = false;
    this.showImageInput = false;
  }

  applyImage() {
    if (!this.imageInputUrl) {
      this.showImageInput = false;
      return;
    }

    if (this.selectedImage) {
      this.selectedImage.src = this.imageInputUrl;
      this.selectedImage.alt = this.imageInputAlt || "";

      this.selectedImage.style.cssText = this.buildImageStyle();

      if (this.imageInputWidth) {
        const hasUnit = /px|%/.test(this.imageInputWidth);
        this.selectedImage.style.width = `${this.imageInputWidth}${hasUnit ? "" : "px"}`;
      }

      const parentLink =
        this.selectedImage.parentElement?.tagName === "A"
          ? (this.selectedImage.parentElement as HTMLAnchorElement)
          : null;

      if (this.imageInputHref && this.imageInputHref.trim()) {
        if (parentLink) {
          parentLink.href = this.imageInputHref;
        } else {
          const link = document.createElement("a");
          link.href = this.imageInputHref;
          link.target = "_blank";
          this.selectedImage.parentNode?.insertBefore(link, this.selectedImage);
          link.appendChild(this.selectedImage);
        }
      } else if (parentLink) {
        const parent = parentLink.parentNode;
        if (parent) {
          parent.insertBefore(this.selectedImage, parentLink);
          parentLink.remove();
        }
      }

      this.selectedImage = null;
      this.showImageInput = false;
      this.onInput();
      return;
    }

    this.restoreSelection();
    const styleAttr = this.buildImageStyle();
    const altAttr = this.imageInputAlt ? ` alt="${this.imageInputAlt}"` : "";

    let imgHtml = `<img src="${this.imageInputUrl}"${altAttr} style="${styleAttr.replace("display:block;", "")}display: block;" />`;

    if (this.imageInputHref && this.imageInputHref.trim()) {
      imgHtml = `<a href="${this.imageInputHref}" target="_blank">${imgHtml}</a>`;
    }

    this.insertHtml(imgHtml);
    this.showImageInput = false;
  }

  cancelImage() {
    this.showImageInput = false;
    this.restoreSelection();
  }

  insertVariable(variable: string) {
    if (this.showSource && this.monacoEditor) {
      const selection = this.monacoEditor.getSelection();
      if (!selection) {
        return;
      }
      this.monacoEditor.executeEdits("insert-variable", [
        {
          range: selection,
          text: variable,
          forceMoveMarkers: true,
        },
      ]);
      this.monacoEditor.focus();
      return;
    }
    if (this.showSource) {
      return;
    }
    this.focusEditor();
    this.insertHtml(variable);
  }

  clear() {
    if (this.showSource && this.monacoEditor) {
      this.monacoEditor.setValue("");
      this.currentContent = "";
      this.onChange("");
      return;
    }
    const editorElement = this.getEditorElement();
    if (editorElement) {
      editorElement.innerHTML = "";
      this.onInput();
    }
  }

  insertTable() {
    const tableHtml = this.createTableHtml(2, 2, 5);
    this.insertHtml(tableHtml);
  }

  insertNestedTable() {
    if (!this.selectedCell) {
      return;
    }

    this.focusEditor();
    const nestedTable = this.createTableHtml(1, 2, 2);

    if (this.isSelectionInsideCell()) {
      document.execCommand("insertHTML", false, nestedTable);
    } else {
      this.selectedCell.insertAdjacentHTML("beforeend", nestedTable);
    }
    this.onInput();
  }

  updateLinkStyle(prop: "textDecoration" | "color", value: string) {
    this.focusEditor();
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    let node = selection.anchorNode;
    while (node && node.nodeName !== "A" && node !== this.getEditorElement()) {
      node = node.parentNode;
    }

    if (node && node.nodeName === "A") {
      const anchor = node as HTMLAnchorElement;
      if (prop === "textDecoration") {
        anchor.style.textDecoration = value;
      } else if (prop === "color") {
        if (
          !value ||
          value.trim() === "transparent" ||
          value.trim() === "rgba(0, 0, 0, 0)"
        ) {
          anchor.style.color = "";
        } else {
          anchor.style.color = this.normalizeColorToHex(value);
        }
      }
      this.onInput();
    }
  }

  updateCellWidth(width: string) {
    if (!this.selectedCell) {
      return;
    }
    const cell = this.selectedCell;
    this.updateCellStyleProperty("width", width, (val) => {
      cell.style.width = val;
      cell.setAttribute("width", val.replace(/px|%/g, ""));
    });
  }

  updateCellHeight(height: string) {
    if (!this.selectedCell) {
      return;
    }
    const cell = this.selectedCell;
    this.updateCellStyleProperty("height", height, (val) => {
      cell.style.height = val;
      cell.setAttribute("height", val.replace(/px|%/g, ""));
    });
  }

  updateTableWidth(width: string) {
    if (!this.selectedTable) {
      return;
    }
    const table = this.selectedTable;
    this.updateCellStyleProperty("width", width, (val) => {
      table.style.width = val;
      table.setAttribute("width", val.replace(/px|%/g, ""));
    });
  }

  updateTableHeight(height: string) {
    if (!this.selectedTable) {
      return;
    }
    const table = this.selectedTable;
    this.updateCellStyleProperty("height", height, (val) => {
      table.style.height = val;
      table.setAttribute("height", val.replace(/px|%/g, ""));
    });
  }

  updateCellPadding(padding: string) {
    if (!this.selectedCell) {
      return;
    }
    const cell = this.selectedCell;
    this.updateCellStyleProperty("padding", padding, (val) => {
      cell.style.padding = val;
    });
  }

  updateCellPaddingSide(
    side: "top" | "right" | "bottom" | "left",
    value: string,
  ) {
    if (!this.selectedCell) {
      return;
    }
    const cell = this.selectedCell;
    const property = `padding${side.charAt(0).toUpperCase() + side.slice(1)}`;
    this.updateCellStyleProperty(property, value, (val) => {
      (cell.style as unknown as Record<string, string>)[property] = val;
    });
  }

  updateLineHeight(height: string) {
    if (!this.selectedCell) {
      return;
    }
    const cell = this.selectedCell;
    this.updateCellStyleProperty("lineHeight", height, (val) => {
      cell.style.lineHeight = val;
    });
  }

  setCellBackgroundColor(color: string) {
    if (!this.selectedCell) {
      return;
    }
    if (
      !color ||
      color.trim() === "transparent" ||
      color.trim() === "rgba(0, 0, 0, 0)"
    ) {
      this.selectedCell.style.backgroundColor = "";
    } else {
      this.selectedCell.style.backgroundColor = this.normalizeColorToHex(color);
    }
    this.onInput();
  }

  removeCellBackgroundColor() {
    if (!this.selectedCell) {
      return;
    }
    this.selectedCell.style.removeProperty("background-color");
    this.onInput();
  }

  hasCellBackgroundColor(): boolean {
    if (!this.selectedCell) {
      return false;
    }
    const bgColor = this.selectedCell.style.backgroundColor;
    return !!(
      bgColor &&
      bgColor !== "transparent" &&
      bgColor !== "rgba(0, 0, 0, 0)"
    );
  }

  getCellBackgroundColor(): string {
    if (!this.selectedCell) {
      return "#ffffff";
    }
    const bgColor = this.selectedCell.style.backgroundColor;
    if (
      !bgColor ||
      bgColor === "transparent" ||
      bgColor === "rgba(0, 0, 0, 0)"
    ) {
      return "#ffffff";
    }
    return this.normalizeColorToHex(bgColor);
  }

  hasCellBorder(): boolean {
    if (!this.selectedCell) {
      return false;
    }
    return !!(
      this.selectedCell.getAttribute("data-border-left") ||
      this.selectedCell.getAttribute("data-border-right") ||
      this.selectedCell.getAttribute("data-border-top") ||
      this.selectedCell.getAttribute("data-border-bottom") ||
      this.selectedCell.getAttribute("data-border-all")
    );
  }

  getCellBorderColor(): string {
    if (!this.selectedCell) {
      return "#000000";
    }

    const borderAll = this.selectedCell.getAttribute("data-border-all");
    if (borderAll) {
      const colorMatch = borderAll.match(
        /#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\)/,
      );
      if (colorMatch) {
        return this.normalizeColorToHex(colorMatch[0]);
      }
    }

    const borderLeft = this.selectedCell.getAttribute("data-border-left");
    if (borderLeft) {
      const colorMatch = borderLeft.match(
        /#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\)/,
      );
      if (colorMatch) {
        return this.normalizeColorToHex(colorMatch[0]);
      }
    }

    const borderRight = this.selectedCell.getAttribute("data-border-right");
    if (borderRight) {
      const colorMatch = borderRight.match(
        /#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\)/,
      );
      if (colorMatch) {
        return this.normalizeColorToHex(colorMatch[0]);
      }
    }

    const borderTop = this.selectedCell.getAttribute("data-border-top");
    if (borderTop) {
      const colorMatch = borderTop.match(
        /#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\)/,
      );
      if (colorMatch) {
        return this.normalizeColorToHex(colorMatch[0]);
      }
    }

    const borderBottom = this.selectedCell.getAttribute("data-border-bottom");
    if (borderBottom) {
      const colorMatch = borderBottom.match(
        /#[0-9a-fA-F]{6}|rgb\([^)]+\)|rgba\([^)]+\)/,
      );
      if (colorMatch) {
        return this.normalizeColorToHex(colorMatch[0]);
      }
    }

    return "#000000";
  }

  removeCellBorder() {
    if (!this.selectedCell) {
      return;
    }
    this.removeAllBorders();
    this.onInput();
  }

  updateCellBorder(side: string, width: string, style: string, color: string) {
    if (!this.selectedCell) {
      return;
    }

    if (side === "none") {
      this.removeAllBorders();
      this.onInput();
      return;
    }

    const realWidth = width
      ? width.endsWith("px")
        ? width
        : `${width}px`
      : "1px";
    const realStyle = style || "solid";
    const realBorderValue = `${realWidth} ${realStyle} ${color}`;
    const dataAttr = `data-border-${side}`;
    this.selectedCell.setAttribute(dataAttr, realBorderValue);

    this.applyBorderStyle(side, realBorderValue);
    this.onInput();
  }

  updateColSpan(span: string) {
    if (!this.selectedCell) {
      return;
    }
    const val = parseInt(span, 10);
    if (!isNaN(val) && val > 0) {
      this.selectedCell.colSpan = val;
      this.onInput();
    }
  }

  updateRowSpan(span: string) {
    if (!this.selectedCell) {
      return;
    }
    const val = parseInt(span, 10);
    if (!isNaN(val) && val > 0) {
      this.selectedCell.rowSpan = val;
      this.onInput();
    }
  }

  updateTableBorderCollapse(collapse: boolean) {
    if (!this.selectedTable) {
      return;
    }
    this.selectedTable.style.borderCollapse = collapse
      ? "collapse"
      : "separate";
    this.onInput();
  }

  updateTableBorderSpacing(spacing: string) {
    if (!this.selectedTable) {
      return;
    }
    this.updateCellStyleProperty("borderSpacing", spacing, (val) => {
      if (this.selectedTable) {
        this.selectedTable.style.borderSpacing = val;
      }
    });
  }

  updateCellVerticalAlign(align: string) {
    if (!this.selectedCell) {
      return;
    }
    const currentAlign =
      this.selectedCell.getAttribute("align") ||
      this.selectedCell.style.textAlign ||
      "";

    this.selectedCell.style.verticalAlign = align;
    this.selectedCell.setAttribute("valign", align);

    if (currentAlign) {
      this.selectedCell.style.textAlign = currentAlign;
      this.selectedCell.setAttribute("align", currentAlign);
    }

    this.currentCellAlign = currentAlign;
    this.currentCellValign = align;
    this.onInput();
  }

  updateCellHorizontalAlign(align: string) {
    if (!this.selectedCell) {
      return;
    }
    const currentVerticalAlign =
      this.selectedCell.getAttribute("valign") ||
      this.selectedCell.style.verticalAlign ||
      "top";

    if (align) {
      this.selectedCell.style.textAlign = align;
      this.selectedCell.style.verticalAlign = currentVerticalAlign;
      this.selectedCell.setAttribute("align", align);
      this.selectedCell.setAttribute("valign", currentVerticalAlign);
    } else {
      this.selectedCell.style.textAlign = "";
      this.selectedCell.removeAttribute("align");
    }

    this.currentCellAlign = align;
    this.currentCellValign = currentVerticalAlign;
    this.onInput();
  }

  private updateCellAlignmentState() {
    if (!this.selectedCell) {
      this.currentCellAlign = "";
      this.currentCellValign = "";
      return;
    }
    this.currentCellAlign =
      this.selectedCell.getAttribute("align") ||
      this.selectedCell.style.textAlign ||
      "";
    this.currentCellValign =
      this.selectedCell.getAttribute("valign") ||
      this.selectedCell.style.verticalAlign ||
      "top";
  }

  deleteTable() {
    if (!this.selectedTable) {
      return;
    }
    this.selectedTable.remove();
    this.selectedTable = null;
    this.selectedCell = null;
    this.onInput();
  }

  updateBackgroundProperty(
    prop: string,
    value: string,
    target?: "cell" | "table",
  ) {
    const el = target === "table" ? this.selectedTable : this.selectedCell;
    if (!el) {
      return;
    }

    if (prop === "backgroundImage") {
      const url = value.trim();
      el.style.backgroundImage = url ? `url(${url})` : "";
    } else {
      (el.style as unknown as Record<string, string>)[prop] = value;
    }
    this.onInput();
  }

  insertRow(where: "before" | "after") {
    const tr = this.getSelectedElement("tr") as HTMLTableRowElement;
    if (!tr) {
      return;
    }

    const newRow = tr.cloneNode(true) as HTMLTableRowElement;
    Array.from(newRow.cells).forEach((cell) => {
      cell.innerHTML = "&nbsp;";
    });

    if (where === "after") {
      tr.after(newRow);
    } else {
      tr.before(newRow);
    }
    this.onInput();
  }

  insertColumn(where: "before" | "after") {
    const td = this.getSelectedElement("td") as HTMLTableCellElement;
    if (!td) {
      return;
    }

    const cellIndex = td.cellIndex;
    const table = this.getSelectedElement("table") as HTMLTableElement;
    if (!table) {
      return;
    }

    Array.from(table.rows).forEach((row) => {
      const newCell = row.insertCell(
        where === "after" ? cellIndex + 1 : cellIndex,
      );
      newCell.style.padding = "5px";
      newCell.innerHTML = "&nbsp;";
      if (td.style.cssText) {
        newCell.style.cssText = td.style.cssText;
      }
    });
    this.onInput();
  }

  deleteRow() {
    const tr = this.getSelectedElement("tr") as HTMLTableRowElement;
    if (tr) {
      tr.remove();
      this.onInput();
    }
  }

  deleteColumn() {
    const td = this.getSelectedElement("td") as HTMLTableCellElement;
    if (!td) {
      return;
    }

    const cellIndex = td.cellIndex;
    const table = this.getSelectedElement("table") as HTMLTableElement;
    if (!table) {
      return;
    }

    Array.from(table.rows).forEach((row) => {
      if (row.cells[cellIndex]) {
        row.deleteCell(cellIndex);
      }
    });
    this.onInput();
  }

  undo() {
    this.focusEditor();
    document.execCommand("undo", false);
    this.onInput();
  }

  redo() {
    this.focusEditor();
    document.execCommand("redo", false);
    this.onInput();
  }

  private getEditorElement(): HTMLDivElement | null {
    return this.editor?.nativeElement || null;
  }

  private focusEditor() {
    const editorElement = this.getEditorElement();
    if (editorElement) {
      editorElement.focus();
    }
  }

  private isNodeInsideEditor(node: Node, editorElement: HTMLElement): boolean {
    let tempNode: Node | null = node;
    while (tempNode) {
      if (tempNode === editorElement) {
        return true;
      }
      tempNode = tempNode.parentElement;
    }
    return false;
  }

  private getSelectedElement(tagName: string): HTMLElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return null;
    }

    const editorElement = this.getEditorElement();
    if (!editorElement) {
      return null;
    }

    let node = selection.getRangeAt(0).commonAncestorContainer as HTMLElement;
    while (node) {
      if (
        node.nodeType === 1 &&
        node.tagName.toLowerCase() === tagName.toLowerCase()
      ) {
        return node;
      }
      node = node.parentElement;
      if (!node || node === editorElement) {
        break;
      }
    }
    return null;
  }

  private findMatchingFont(fontName: string): string {
    if (!fontName) {
      return "";
    }

    const cleanFontName = fontName.replace(/['"]/g, "");
    const exactMatch = this.fontFamilies.find(
      (f) => f.value.replace(/['"]/g, "") === cleanFontName,
    );
    if (exactMatch) {
      return exactMatch.value;
    }

    const partialMatch = this.fontFamilies.find(
      (f) => f.value.includes(cleanFontName) || cleanFontName.includes(f.name),
    );
    return partialMatch?.value || "";
  }

  private processHtmlForEditor(html: string): string {
    if (!html) {
      return "";
    }

    const temp = document.createElement("div");
    temp.innerHTML = html;
    const cells = temp.querySelectorAll<HTMLTableCellElement>(
      'td[style*="border"]',
    );

    cells.forEach((cell) => {
      this.processCellBordersForEditor(cell);
    });
    this.convertLegacyFontTags(temp);

    return temp.innerHTML;
  }

  private processHtmlForOutput(html: string): string {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    this.convertLegacyFontTags(temp);
    const cells = temp.querySelectorAll<HTMLTableCellElement>(
      "td[data-border-left], td[data-border-right], td[data-border-top], td[data-border-bottom], td[data-border-all]",
    );

    cells.forEach((cell) => {
      this.processCellBordersForOutput(cell);
    });

    const allElements = temp.querySelectorAll<HTMLElement>("*");
    allElements.forEach((el) => {
      if (el.style.color) {
        const color = el.style.color.trim();
        if (color === "transparent" || color === "rgba(0, 0, 0, 0)") {
          el.style.removeProperty("color");
        } else {
          el.style.color = this.normalizeColorToHex(el.style.color);
        }
      }
      if (el.style.backgroundColor) {
        const bgColor = el.style.backgroundColor.trim();
        if (bgColor === "transparent" || bgColor === "rgba(0, 0, 0, 0)") {
          el.style.removeProperty("background-color");
        } else {
          el.style.backgroundColor = this.normalizeColorToHex(
            el.style.backgroundColor,
          );
        }
      }
    });

    return temp.innerHTML;
  }

  private normalizeLegacyFontMarkup(html: string): string {
    if (!html || !/<font[\s>]/i.test(html)) {
      return html;
    }
    const temp = document.createElement("div");
    temp.innerHTML = html;
    this.convertLegacyFontTags(temp);
    return temp.innerHTML;
  }

  private convertLegacyFontTags(container: HTMLElement) {
    const fonts = container.querySelectorAll("font");
    fonts.forEach((fontEl) => {
      const span = document.createElement("span");
      const existingStyle = fontEl.getAttribute("style");
      if (existingStyle?.trim()) {
        span.setAttribute("style", existingStyle);
      }

      const color = fontEl.getAttribute("color");
      if (color?.trim()) {
        span.style.color = this.normalizeColorToHex(color);
      }

      const face = fontEl.getAttribute("face");
      if (face?.trim()) {
        span.style.fontFamily = face;
      }

      const size = fontEl.getAttribute("size");
      const mappedSize = this.mapLegacyFontSizeToPx(size);
      if (mappedSize) {
        span.style.fontSize = mappedSize;
      }

      while (fontEl.firstChild) {
        span.appendChild(fontEl.firstChild);
      }
      fontEl.replaceWith(span);
    });
  }

  private mapLegacyFontSizeToPx(size: string | null): string {
    if (!size) {
      return "";
    }

    const normalizedSize = size.trim();
    if (LEGACY_FONT_SIZE_TO_PX[normalizedSize]) {
      return LEGACY_FONT_SIZE_TO_PX[normalizedSize];
    }

    if (/^\d+(px|em|rem|%)$/i.test(normalizedSize)) {
      return normalizedSize;
    }

    if (/^\d+$/.test(normalizedSize)) {
      return `${normalizedSize}px`;
    }

    return "";
  }

  private setStyleWithCssMode(enabled: boolean) {
    try {
      document.execCommand("styleWithCSS", false, enabled ? "true" : "false");
    } catch (error) {
      void error;
    }
  }

  private processCellBordersForEditor(cell: HTMLTableCellElement) {
    const style = cell.style;
    const extractBorderValue = (borderValue: string): string | null => {
      if (!borderValue || borderValue === "none") {
        return null;
      }
      return borderValue;
    };

    if (style.border && style.border !== "none") {
      const borderValue = extractBorderValue(style.border);
      if (borderValue) {
        cell.setAttribute("data-border-all", borderValue);
      }
      return;
    }

    const borderSides: Array<{ prop: string; attr: string }> = [
      { prop: "borderLeft", attr: "left" },
      { prop: "borderRight", attr: "right" },
      { prop: "borderTop", attr: "top" },
      { prop: "borderBottom", attr: "bottom" },
    ];

    borderSides.forEach(({ prop, attr }) => {
      const cssName = prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
      const borderValue = extractBorderValue(style.getPropertyValue(cssName));
      if (borderValue) {
        cell.setAttribute(`data-border-${attr}`, borderValue);
      }
    });
  }

  private processCellBordersForOutput(cell: HTMLTableCellElement) {
    const borderAll = cell.getAttribute("data-border-all");
    if (borderAll) {
      cell.style.border = this.normalizeBorderColorToHex(borderAll);
      cell.removeAttribute("data-border-all");
      return;
    }

    const borderSides: Array<{ prop: string; attr: string }> = [
      { prop: "borderLeft", attr: "left" },
      { prop: "borderRight", attr: "right" },
      { prop: "borderTop", attr: "top" },
      { prop: "borderBottom", attr: "bottom" },
    ];

    borderSides.forEach(({ prop, attr }) => {
      const borderValue = cell.getAttribute(`data-border-${attr}`);
      if (borderValue) {
        (cell.style as unknown as Record<string, string>)[prop] =
          this.normalizeBorderColorToHex(borderValue);
        cell.removeAttribute(`data-border-${attr}`);
      }
    });
  }

  private normalizeBorderColorToHex(borderValue: string): string {
    const parts = borderValue.split(/\s+/);
    if (parts.length < 3) {
      return borderValue;
    }

    const width = parts[0];
    const style = parts[1];
    const color = parts.slice(2).join(" ");
    const hexColor = this.normalizeColorToHex(color);

    return `${width} ${style} ${hexColor}`;
  }

  getCellBorderStyle(): string {
    if (!this.selectedCell) {
      return "solid";
    }
    const borderAll = this.selectedCell.getAttribute("data-border-all");
    if (borderAll) {
      const parts = borderAll.split(" ");
      if (parts.length >= 2) {
        return parts[1];
      }
    }
    return "solid";
  }

  getCellBorderWidth(): string {
    if (!this.selectedCell) {
      return "";
    }
    const borderAll = this.selectedCell.getAttribute("data-border-all");
    if (borderAll) {
      const parts = borderAll.split(" ");
      if (parts.length >= 1) {
        return parts[0].replace("px", "");
      }
    }
    return "";
  }

  getTableBorderCollapse(): boolean {
    return this.selectedTable?.style.borderCollapse === "collapse";
  }

  private saveSelection() {
    const selection = window.getSelection();
    if (selection?.rangeCount) {
      this.savedRange = selection.getRangeAt(0);
    }
  }

  private restoreSelection() {
    if (!this.savedRange) {
      return;
    }

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }
    this.focusEditor();
  }

  private isSelectionInsideCell(): boolean {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return false;
    }

    let node = selection.anchorNode;
    while (node) {
      if (node === this.selectedCell) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  private createTableHtml(rows: number, cols: number, padding: number): string {
    const rowsHtml = Array.from(
      { length: rows },
      () =>
        `<tr>${Array.from(
          { length: cols },
          () => `<td style="padding: ${padding}px;">&nbsp;</td>`,
        ).join("")}</tr>`,
    ).join("");

    return `<table style="border-collapse: collapse; width: 100%;" border="0" cellpadding="0" cellspacing="0">${rowsHtml}</table><br>`;
  }

  private updateCellStyleProperty(
    property: string,
    value: string,
    updater: (normalizedValue: string) => void,
  ) {
    if (!value) {
      updater("");
      this.onInput();
      return;
    }

    const normalizedValue = this.normalizeSizeValue(
      value,
      property === "lineHeight" || property === "padding",
    );
    updater(normalizedValue);
    this.onInput();
  }

  private normalizeSizeValue(value: string, allowEm: boolean): string {
    const units = allowEm ? ["px", "%", "em"] : ["px", "%"];
    const hasUnit = units.some((unit) => value.endsWith(unit));
    return hasUnit ? value : `${value}px`;
  }

  private removeAllBorders() {
    if (!this.selectedCell) {
      return;
    }

    const cell = this.selectedCell;
    cell.style.border = "";
    cell.style.borderLeft = "";
    cell.style.borderRight = "";
    cell.style.borderTop = "";
    cell.style.borderBottom = "";

    ["left", "right", "top", "bottom", "all"].forEach((side) => {
      cell.removeAttribute(`data-border-${side}`);
    });
  }

  private applyBorderStyle(side: string, visualBorder: string) {
    if (!this.selectedCell) {
      return;
    }

    const borderMap: Record<string, string> = {
      all: "border",
      left: "borderLeft",
      right: "borderRight",
      top: "borderTop",
      bottom: "borderBottom",
    };

    const styleProp = borderMap[side];
    if (styleProp) {
      (this.selectedCell.style as unknown as Record<string, string>)[
        styleProp
      ] = visualBorder;
    }
  }

  private buildImageStyle(): string {
    let style = "display:block; border:0;";
    if (this.imageInputWidth) {
      const hasUnit = /px|%/.test(this.imageInputWidth);
      style += ` width: ${this.imageInputWidth}${hasUnit ? "" : "px"};`;
    }
    return style;
  }

  mergeCells() {
    if (!this.selectedCell) {
      return;
    }

    const cell = this.selectedCell;
    const table = cell.closest("table");
    if (!table) {
      return;
    }

    const row = cell.parentElement as HTMLTableRowElement;
    const rowIndex = Array.from(table.rows).indexOf(row);
    const cellIndex = Array.from(row.cells).indexOf(cell);

    let adjacentCell: HTMLTableCellElement | null = null;
    let mergeDirection: "right" | "down" | null = null;

    const rightCell = this.getCellAtPosition(
      table,
      rowIndex,
      cellIndex + (cell.colSpan || 1),
    );
    const downCell = this.getCellAtPosition(
      table,
      rowIndex + (cell.rowSpan || 1),
      cellIndex,
    );

    if (rightCell) {
      adjacentCell = rightCell;
      mergeDirection = "right";
    } else if (downCell) {
      adjacentCell = downCell;
      mergeDirection = "down";
    }

    if (!adjacentCell || !mergeDirection) {
      return;
    }

    if (mergeDirection === "right") {
      cell.colSpan = (cell.colSpan || 1) + (adjacentCell.colSpan || 1);
      cell.innerHTML += adjacentCell.innerHTML;
      adjacentCell.remove();
    } else {
      cell.rowSpan = (cell.rowSpan || 1) + (adjacentCell.rowSpan || 1);
      cell.innerHTML += adjacentCell.innerHTML;
      adjacentCell.remove();
    }

    this.onInput();
  }

  private getCellAtPosition(
    table: HTMLTableElement,
    rowIndex: number,
    cellIndex: number,
  ): HTMLTableCellElement | null {
    if (rowIndex < 0 || rowIndex >= table.rows.length) {
      return null;
    }

    const row = table.rows[rowIndex];
    let currentIndex = 0;

    for (const cell of Array.from(row.cells)) {
      const cellElement = cell;
      const cellColSpan = cellElement.colSpan || 1;

      if (currentIndex <= cellIndex && cellIndex < currentIndex + cellColSpan) {
        if (currentIndex === cellIndex) {
          return cellElement;
        }
        return null;
      }

      currentIndex += cellColSpan;
    }

    return null;
  }

  splitCell() {
    if (!this.selectedCell) {
      return;
    }

    const cell = this.selectedCell;
    const rowSpan = cell.rowSpan || 1;
    const colSpan = cell.colSpan || 1;

    if (rowSpan === 1 && colSpan === 1) {
      return;
    }

    const table = cell.closest("table");
    if (!table) {
      return;
    }

    const row = cell.parentElement as HTMLTableRowElement;
    const rowIndex = Array.from(table.rows).indexOf(row);
    const cellIndex = Array.from(row.cells).indexOf(cell);

    cell.rowSpan = 1;
    cell.colSpan = 1;

    for (let r = 0; r < rowSpan; r++) {
      const currentRow = table.rows[rowIndex + r];
      if (!currentRow) {
        continue;
      }

      for (let c = 0; c < colSpan; c++) {
        if (r === 0 && c === 0) {
          continue;
        }

        let insertIndex = cellIndex + c;
        const newCell = document.createElement("td");
        newCell.innerHTML = "&nbsp;";
        newCell.style.padding = cell.style.padding || "5px";

        if (c === 0 && r > 0) {
          const prevRow = table.rows[rowIndex + r - 1];
          if (prevRow && prevRow.cells[cellIndex]) {
            insertIndex =
              Array.from(currentRow.cells).indexOf(prevRow.cells[cellIndex]) +
              1;
          }
        }

        if (insertIndex < currentRow.cells.length) {
          currentRow.insertBefore(newCell, currentRow.cells[insertIndex]);
        } else {
          currentRow.appendChild(newCell);
        }
      }
    }

    this.onInput();
  }
}
